/**
 * Otto Engine — shared portion + pack cadence engine.
 *
 * Single source of truth for the math used by both the onboarding configurator
 * (otto-configurator-v2.js) and the customer portal (otto-portal.js). Ports the
 * `delivery-cadence-model-v2` canvas:
 *
 *   - per-dog daily intake = weight x bodyweight% x stage x activity x goal
 *   - daily oz snapped onto a fixed portion ladder (WFR-style: 1-oz steps under
 *     6 oz, 2-oz steps to 16 oz, 4-oz steps to 64 oz)
 *   - multi-dog households merge into one "pack": portions sum, one shared
 *     cadence (one contract -> one billing date -> one shipment)
 *   - every cadence inside the min-lb / max-days window is offered, labeled
 *     with the freezer profile it requires; recommended = longest cadence that
 *     fits the household's selected freezer.
 *
 * Backwards compatibility: exposes the legacy OttoCfgV2Cadence surface
 * (evaluateCadence / getCadenceOptions / roundDailyIntake / bagSplit ...) so
 * pages that load only this file keep working. If otto-configurator-v2-cadence
 * is already on the page, it is left untouched.
 */
(function (global) {
  var DEFAULT_CADENCES = [
    { key: 'weekly', label: 'Every week', weeks: 1, days: 7 },
    { key: 'biweekly', label: 'Every 2 weeks', weeks: 2, days: 14 },
    { key: 'threeweek', label: 'Every 3 weeks', weeks: 3, days: 21 },
    { key: 'fourweek', label: 'Every 4 weeks', weeks: 4, days: 28 },
    { key: 'fiveweek', label: 'Every 5 weeks', weeks: 5, days: 35 },
    { key: 'sixweek', label: 'Every 6 weeks', weeks: 6, days: 42 },
    { key: 'sevenweek', label: 'Every 7 weeks', weeks: 7, days: 49 },
    { key: 'eightweek', label: 'Every 8 weeks', weeks: 8, days: 56 },
    { key: 'nineweek', label: 'Every 9 weeks', weeks: 9, days: 63 },
    { key: 'tenweek', label: 'Every 10 weeks', weeks: 10, days: 70 },
    { key: 'elevenweek', label: 'Every 11 weeks', weeks: 11, days: 77 },
    { key: 'twelveweek', label: 'Every 12 weeks', weeks: 12, days: 84 }
  ];

  // We Feed Raw configurator portion ladder, oz/day.
  var DEFAULT_PORTION_LADDER_OZ = [
    2, 3, 4, 5, 6, 8, 10, 12, 14, 16, 20, 24, 28, 32, 36, 40, 44, 48, 52, 56, 60, 64
  ];

  function num(value, fallback) {
    var n = parseFloat(value);
    return isFinite(n) ? n : fallback;
  }

  /* ------------------------------------------------------------------ */
  /* Portion ladder                                                      */
  /* ------------------------------------------------------------------ */

  function parsePortionLadder(raw, fallback) {
    var fb = Array.isArray(fallback) && fallback.length ? fallback : DEFAULT_PORTION_LADDER_OZ;
    if (Array.isArray(raw)) {
      var fromArr = raw.map(Number).filter(function (n) { return isFinite(n) && n > 0; });
      if (fromArr.length) return dedupeSorted(fromArr);
      return fb.slice();
    }
    if (!raw) return fb.slice();
    var list = String(raw)
      .split(/[,\s]+/)
      .map(function (x) { return parseFloat(x); })
      .filter(function (n) { return isFinite(n) && n > 0; });
    return list.length ? dedupeSorted(list) : fb.slice();
  }

  function dedupeSorted(list) {
    var seen = {};
    var out = [];
    list.forEach(function (v) {
      if (!seen[v]) { seen[v] = true; out.push(v); }
    });
    out.sort(function (a, b) { return a - b; });
    return out;
  }

  /**
   * Snap a raw oz/day value onto the ladder.
   * policy: 'nearest' (ties round up — never short on a tie), 'up', 'down'.
   */
  function snapOz(rawOz, ladder, policy) {
    var rungs = Array.isArray(ladder) && ladder.length ? ladder : DEFAULT_PORTION_LADDER_OZ;
    var x = Number(rawOz || 0);
    if (!(x > 0)) return 0;
    var min = rungs[0];
    var max = rungs[rungs.length - 1];
    if (x >= max) return max;
    if (x <= min) return min;
    var p = String(policy || 'nearest').trim().toLowerCase();
    var i;
    if (p === 'up') {
      for (i = 0; i < rungs.length; i++) if (rungs[i] >= x) return rungs[i];
      return max;
    }
    if (p === 'down') {
      var best = min;
      for (i = 0; i < rungs.length; i++) {
        if (rungs[i] <= x) best = rungs[i];
        else break;
      }
      return best;
    }
    var bestV = rungs[0];
    var bestD = Math.abs(rungs[0] - x);
    for (i = 0; i < rungs.length; i++) {
      var d = Math.abs(rungs[i] - x);
      if (d < bestD - 1e-9 || (Math.abs(d - bestD) <= 1e-9 && rungs[i] > bestV)) {
        bestV = rungs[i];
        bestD = d;
      }
    }
    return bestV;
  }

  /* ------------------------------------------------------------------ */
  /* Age-derived life stages                                             */
  /* ------------------------------------------------------------------ */

  var STAGE_LABELS = {
    young_puppy: 'Young Puppy',
    growing_puppy: 'Growing Puppy',
    juvenile: 'Juvenile',
    young_adult: 'Young Adult',
    adult: 'Adult',
    senior: 'Senior',
    puppy: 'Puppy' // legacy 3-stage key
  };

  /* ------------------------------------------------------------------ */
  /* Breed size classes                                                  */
  /* ------------------------------------------------------------------ */

  /**
   * Breed → size class, derived silently (never asked). Size shifts the
   * later stage boundaries: toy breeds mature fast and go senior late;
   * giant breeds keep growing past 18 months and go senior around 6 years.
   * Classes only nudge boundaries, so edge-of-class judgment calls are low
   * stakes. Unlisted/custom breeds fall back to weight bands (grown dogs)
   * or medium (growing dogs) via sizeClassForDog().
   */
  var SIZE_CLASS_BREEDS = {
    toy: 'Affenpinscher|Biewer Terrier|Bolognese|Brussels Griffon|Chihuahua|Chinese Crested|Chipoo|Chiweenie|Coton de Tulear|English Toy Spaniel|Italian Greyhound|Japanese Chin|Japanese Terrier|King Charles Spaniel|Maltese|Maltichon|Maltipoo|Miniature Pinscher|Morkie|Papillon|Pekingese|Pomapoo|Pomeranian|Russian Toy|Silky Terrier|Australian Silky Terrier|Toy Fox Terrier|Toy Manchester Terrier|Toy Poodle|Volpino Italiano|Yorkipoo|Yorkshire Terrier',
    small: 'Alaskan Klee Kai|American Eskimo Dog|American Hairless Terrier|Australian Terrier|Basenji|Beagle|Bedlington Terrier|Bichon Frise|Bichpoo (Poochon)|Border Terrier|Boston Terrier|Brazilian Terrier|Cairn Terrier|Cardigan Welsh Corgi|Cavachon|Cavalier King Charles Spaniel|Cavapoo|Cesky Terrier|Cirneco dell\'Etna|Cockalier|Cockapoo|Cocker Spaniel|American Cocker Spaniel|Corgipoo|Dachshund|Dachsador|Dandie Dinmont Terrier|Danish-Swedish Farmdog|Drever|English Cocker Spaniel|French Bulldog|German Spitz|Havanese|Havapoo|Icelandic Sheepdog|Jack Russell Terrier|Jagdterrier|Japanese Spitz|Lakeland Terrier|Lancashire Heeler|Lhasa Apso|L\u00f6wchen|Manchester Terrier|Miniature American Shepherd|Miniature Bull Terrier|Miniature Poodle|Miniature Schnauzer|Mudi|Norfolk Terrier|Norrbottenspets|Norwegian Lundehund|Norwich Terrier|Parson Russell Terrier|Pembroke Welsh Corgi|Pocket Beagle|Pomsky|Pug|Puggle|Pumi|Pyrenean Shepherd|Rat Terrier|Russell Terrier|Schipperke|Schnoodle|Scottish Terrier|Sealyham Terrier|Shetland Sheepdog|Shiba Inu|Shih Tzu|Shihpoo|Smooth Fox Terrier|Swedish Vallhund|Teddy Roosevelt Terrier|Tibetan Spaniel|Tibetan Terrier|Welsh Terrier|West Highland White Terrier|Wire Fox Terrier|Nederlandse Kooikerhondje|Kooikerhondje',
    medium: 'American Leopard Hound|American Pit Bull Terrier|American Staffordshire Terrier|American Water Spaniel|Appenzeller Sennenhund|Aussiedoodle|Australian Cattle Dog|Australian Kelpie|Australian Shepherd|Australian Stumpy Tail Cattle Dog|Azawakh|Barbado da Terceira|Barbet|Basset Fauve de Bretagne|Basset Hound|Bavarian Mountain Scent Hound|Bearded Collie|Blue Picardy Spaniel|Bohemian Shepherd|Border Collie|Bordoodle|Bouvier des Ardennes|Boxerdoodle|Boykin Spaniel|Braque du Bourbonnais|Braque Francais Pyrenean|Braque Saint-Germain|Brittany|Bull Terrier|Bulldog|English Bulldog|Canaan Dog|Carolina Dog|Chinese Shar-Pei|Croatian Sheepdog|Czechoslovakian Vlciak|Deutscher Wachtelhund|Drentsche Patrijshond|English Springer Spaniel|Entlebucher Mountain Dog|Eurasier|Field Spaniel|Finnish Lapphund|Finnish Spitz|French Spaniel|German Pinscher|Glen of Imaal Terrier|Grand Basset Griffon Vend\u00e9en|Hamiltonstovare|Harrier|Hokkaido|Ibizan Hound|Irish Red and White Setter|Irish Terrier|Kai Ken|Karelian Bear Dog|Keeshond|Kerry Blue Terrier|Kishu Ken|Korean Jindo Dog|Kromfohrlander|Lagotto Romagnolo|Lapponian Herder|Mountain Cur|Norwegian Buhund|Norwegian Elkhound|Nova Scotia Duck Tolling Retriever|Peruvian Inca Orchid|Petit Basset Griffon Vend\u00e9en|Pharaoh Hound|Pitbull|Plott Hound|Pont-Audemer Spaniel|Skye Terrier|Polish Lowland Sheepdog|Poodle|Standard Poodle|Porcelaine|Portuguese Podengo|Portuguese Pointer|Portuguese Sheepdog|Portuguese Water Dog|Puli|Saluki|Samoyed|Schapendoes|Segugio Italiano|Shikoku Ken|Siberian Husky|Silken Windhound|Sloughi|Slovakian Wirehaired Pointer|Slovensky Kopov|Small Munsterlander|Soft Coated Wheaten Terrier|Spanish Water Dog|Stabyhoun|Staffordshire Bull Terrier|Standard Schnauzer|Sussex Spaniel|Swedish Lapphund|Taiwan Dog|Transylvanian Hound|Treeing Tennessee Brindle|Vizsla|Welsh Springer Spaniel|Wetterhoun|Whippet|Whoodle|Wirehaired Pointing Griffon|Wirehaired Vizsla|Working Kelpie|Xoloitzcuintli|Yakutian Laika',
    large: 'Afghan Hound|Airedale Terrier|Akita|Alaskan Malamute|American Bulldog|American English Coonhound|American Foxhound|Beauceron|Belgian Laekenois|Belgian Malinois|Belgian Sheepdog|Belgian Tervuren|Bergamasco Sheepdog|Bergamasco|Berger Picard|Bernedoodle|Bernese Mountain Dog|Black and Tan Coonhound|Black Russian Terrier|Bloodhound|Bluetick Coonhound|Borzoi|Bouvier des Flandres|Boxer|Bracco Italiano|Briard|Canadian Eskimo Dog|Catahoula Leopard Dog|Chesapeake Bay Retriever|Chinook|Chow Chow|Clumber Spaniel|Collie|Curly-Coated Retriever|Dalmatian|Doberman Pinscher|Dogo Argentino|Double Doodle|Dutch Shepherd|English Foxhound|English Setter|Flat-Coated Retriever|German Longhaired Pointer|German Shepherd Dog|German Shorthaired Pointer|German Wirehaired Pointer|Giant Schnauzer|Goldador|Golden Retriever|Goldendoodle|Gordon Setter|Greyhound|Hanoverian Scenthound|Hovawart|Irish Setter|Irish Water Spaniel|Labradoodle|Labrador Retriever|Large Munsterlander|Maremma Sheepdog|Newfypoo|Old English Sheepdog|Otterhound|Pointer|Pudelpointer|Pyredoodle|Redbone Coonhound|Rhodesian Ridgeback|Rottweiler|Sheepadoodle|Slovensky Cuvac|Spinone Italiano|Thai Ridgeback|Tornjak|Treeing Walker Coonhound|Weimaraner',
    giant: 'Akbash|Anatolian Shepherd Dog|Boerboel|Broholmer|Bullmastiff|Cane Corso|Caucasian Shepherd Dog|Central Asian Shepherd Dog|Deerhound|Dogue de Bordeaux|English Mastiff|Estrela Mountain Dog|Great Dane|Great Pyrenees|Greater Swiss Mountain Dog|Irish Wolfhound|Komondor|Kuvasz|Leonberger|Mastiff|Neapolitan Mastiff|Newfoundland|Presa Canario|Pyrenean Mastiff|Rafeiro do Alentejo|Romanian Mioritic Shepherd Dog|Saint Berdoodle|Saint Bernard|Scottish Deerhound|Spanish Mastiff|Tibetan Mastiff|Tosa|Tosa Inu'
  };

  function normalizeBreedForSize(value) {
    var s = String(value || '').toLowerCase().trim();
    try {
      s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    } catch (_e) {}
    return s.replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
  }

  var BREED_SIZE_LOOKUP = null;
  function breedSizeLookup() {
    if (BREED_SIZE_LOOKUP) return BREED_SIZE_LOOKUP;
    BREED_SIZE_LOOKUP = {};
    Object.keys(SIZE_CLASS_BREEDS).forEach(function (cls) {
      SIZE_CLASS_BREEDS[cls].split('|').forEach(function (breed) {
        var key = normalizeBreedForSize(breed);
        if (key) BREED_SIZE_LOOKUP[key] = cls;
      });
    });
    return BREED_SIZE_LOOKUP;
  }

  /** Size class from a breed name, or '' when unknown. */
  function breedSizeClass(breed) {
    var key = normalizeBreedForSize(breed);
    if (!key) return '';
    var cls = breedSizeLookup()[key];
    if (cls) return cls;
    // Keyword hints for custom-typed breeds.
    if (/(^|\s)(teacup|toy)(\s|$)/.test(key)) return 'toy';
    if (/(^|\s)(miniature|mini)(\s|$)/.test(key)) return 'small';
    if (/(^|\s)giant(\s|$)/.test(key)) return 'giant';
    return '';
  }

  /** Adult-weight bands for unknown breeds (lb). */
  function sizeClassFromWeight(weightLb) {
    var w = Number(weightLb || 0);
    if (!(w > 0)) return '';
    if (w < 12) return 'toy';
    if (w < 25) return 'small';
    if (w < 55) return 'medium';
    if (w < 90) return 'large';
    return 'giant';
  }

  /**
   * Effective size class for a dog. Breed map first; for unknown breeds,
   * current weight is a reliable proxy only once the dog is grown (or when
   * we have no birthdate at all — legacy adult profiles). Growing dogs with
   * unknown breed default to medium and self-correct as they mature.
   */
  function sizeClassForDog(dog, settings, now) {
    var d = dog || {};
    var byBreed = breedSizeClass(d.breed);
    if (byBreed) return byBreed;
    var months = ageMonthsFromBirthdate(d.birthdate, now);
    var adultStart = num((settings || {}).stage_adult_start_months, 24);
    if (months == null || months >= adultStart) {
      return sizeClassFromWeight(d.weight) || 'medium';
    }
    return 'medium';
  }

  /**
   * Boundary scaling per size class, applied to the merchant-tunable medium
   * baseline. Early growth boundaries are size-agnostic; maturity and senior
   * boundaries shift: toy ~10/18mo adult, senior ~11yr; giant ~18/30mo
   * adult, senior ~6yr.
   */
  var SIZE_BOUNDS_FACTORS = {
    toy: { youngAdult: 10 / 12, adult: 18 / 24, senior: 132 / 84 },
    small: { youngAdult: 11 / 12, adult: 20 / 24, senior: 120 / 84 },
    medium: { youngAdult: 1, adult: 1, senior: 1 },
    large: { youngAdult: 15 / 12, adult: 1, senior: 78 / 84 },
    giant: { youngAdult: 18 / 12, adult: 30 / 24, senior: 72 / 84 }
  };

  /** Stage start boundaries in months for a size class ('' → medium). */
  function stageBoundsMonths(settings, sizeClass) {
    var s = settings || {};
    var f = SIZE_BOUNDS_FACTORS[String(sizeClass || '').toLowerCase()] || SIZE_BOUNDS_FACTORS.medium;
    return {
      growing: num(s.stage_growing_puppy_start_months, 4),
      juvenile: num(s.stage_juvenile_start_months, 6),
      youngAdult: num(s.stage_young_adult_start_months, 12) * f.youngAdult,
      adult: num(s.stage_adult_start_months, 24) * f.adult,
      senior: num(s.stage_senior_start_months, 84) * f.senior
    };
  }

  /**
   * Age in months from a birthdate string ('YYYY-MM' or 'YYYY-MM-DD').
   * Returns null when unparseable; future dates clamp to 0.
   */
  function ageMonthsFromBirthdate(birthdate, now) {
    var m = String(birthdate || '').trim().match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/);
    if (!m) return null;
    var y = parseInt(m[1], 10);
    var mo = parseInt(m[2], 10);
    var d = m[3] ? parseInt(m[3], 10) : 15;
    if (mo < 1 || mo > 12 || y < 1980) return null;
    var ref = now instanceof Date ? now : new Date();
    var months = (ref.getFullYear() - y) * 12 + (ref.getMonth() + 1 - mo) + (ref.getDate() - d) / 30.44;
    return months > 0 ? months : 0;
  }

  function stageFromAgeMonths(months, settings, sizeClass) {
    var n = Number(months);
    if (!isFinite(n) || n < 0) return '';
    var b = stageBoundsMonths(settings, sizeClass);
    if (n < b.growing) return 'young_puppy';
    if (n < b.juvenile) return 'growing_puppy';
    if (n < b.youngAdult) return 'juvenile';
    if (n < b.adult) return 'young_adult';
    if (n < b.senior) return 'adult';
    return 'senior';
  }

  /**
   * Synthetic 'YYYY-MM' birthdate at the midpoint of a stage bucket, for
   * owners who don't know the actual birthday.
   */
  function birthdateForStage(stage, settings, now, sizeClass) {
    var b = stageBoundsMonths(settings, sizeClass);
    var mids = {
      young_puppy: (2 + b.growing) / 2,
      growing_puppy: (b.growing + b.juvenile) / 2,
      juvenile: (b.juvenile + b.youngAdult) / 2,
      young_adult: (b.youngAdult + b.adult) / 2,
      adult: (b.adult + b.senior) / 2,
      senior: b.senior + 24
    };
    var months = mids[String(stage || '').trim().toLowerCase()];
    if (!isFinite(months)) return '';
    var ref = now instanceof Date ? new Date(now.getTime()) : new Date();
    ref.setDate(15);
    ref.setMonth(ref.getMonth() - Math.round(months));
    var mo = ref.getMonth() + 1;
    return ref.getFullYear() + '-' + (mo < 10 ? '0' : '') + mo;
  }

  /**
   * Effective life stage for a dog: derived from `birthdate` when present
   * (so dogs age automatically), else the stored `life_stage`/`stage` string.
   * Stage boundaries are size-aware via the dog's breed (or weight fallback).
   */
  function resolveLifeStage(dog, settings, now) {
    var d = dog || {};
    if (d.birthdate) {
      var months = ageMonthsFromBirthdate(d.birthdate, now);
      if (months != null) {
        var derived = stageFromAgeMonths(months, settings, sizeClassForDog(d, settings, now));
        if (derived) return derived;
      }
    }
    var s = String(d.life_stage || d.stage || '').trim().toLowerCase();
    return s || 'adult';
  }

  /**
   * Growth stages feed a direct percent of current body weight and skip the
   * activity/goal questions. Young Adult is included: it feeds its own taper
   * percent, and a 1-2 yr old filling out doesn't need a goal either.
   */
  function isGrowthStage(stage) {
    var s = String(stage || '').trim().toLowerCase();
    return s === 'young_puppy' || s === 'growing_puppy' || s === 'juvenile' || s === 'young_adult';
  }

  /**
   * Recommended feedings per day for young growth stages: small stomachs need
   * small, frequent meals — 4x/day at 8-16 weeks, 3x/day at 4-6 months.
   * Returns 0 for other stages (household meals-per-day setting applies).
   */
  function recommendedMealsPerDay(stage) {
    var s = String(stage || '').trim().toLowerCase();
    if (s === 'young_puppy') return 4;
    if (s === 'growing_puppy') return 3;
    return 0;
  }

  function stageLabel(stage) {
    var s = String(stage || '').trim().toLowerCase();
    if (STAGE_LABELS[s]) return STAGE_LABELS[s];
    if (!s) return '';
    return s.split('_').map(function (part) {
      return part.charAt(0).toUpperCase() + part.slice(1);
    }).join(' ');
  }

  /** Human age from months: "10 weeks", "7 months", "2 years", "3½ years". */
  function formatAgeMonths(months) {
    var n = Number(months);
    if (!isFinite(n) || n < 0) return '';
    if (n < 4) {
      var weeks = Math.max(1, Math.round(n * 4.345));
      return weeks + (weeks === 1 ? ' week' : ' weeks');
    }
    if (n < 24) {
      var mo = Math.round(n);
      return mo + (mo === 1 ? ' month' : ' months');
    }
    var years = n / 12;
    var rounded = Math.round(years * 2) / 2;
    var label = (rounded % 1 === 0) ? String(rounded) : (Math.floor(rounded) + '\u00BD');
    return label + (rounded === 1 ? ' year' : ' years');
  }

  /* ------------------------------------------------------------------ */
  /* Per-dog intake                                                      */
  /* ------------------------------------------------------------------ */

  /**
   * Life-stage multiplier for non-growth stages. Adult feeds the
   * weight-banded baseline as-is; only senior gets a multiplier.
   * (Young Adult never reaches here — it's a growth taper stage.)
   */
  function stageMult(settings, stage) {
    var s = String(stage || 'adult').trim().toLowerCase();
    if (s === 'senior') return num(settings.life_stage_senior_multiplier, 80) / 100;
    return 1;
  }

  /**
   * Direct feed percent of current body weight for growth stages, age-based
   * rather than weight-banded (midpoints of the recommended ranges:
   * Young Puppy 8-10%, Growing Puppy 6-8%, Juvenile 4-6%). Young Adult acts
   * as the taper stage between Juvenile and the adult weight bands so big
   * dogs step down instead of falling off a cliff. Legacy dogs saved with
   * the old 3-stage "puppy" value and no birthdate feed the Growing Puppy
   * percent. Returns 0 for non-growth stages.
   */
  function stageFeedRate(settings, stage) {
    var s = String(stage || '').trim().toLowerCase();
    if (s === 'young_puppy') return num(settings.feed_pct_young_puppy, 9) / 100;
    if (s === 'growing_puppy' || s === 'puppy') return num(settings.feed_pct_growing_puppy, 7) / 100;
    if (s === 'juvenile') return num(settings.feed_pct_juvenile, 5) / 100;
    if (s === 'young_adult') return num(settings.feed_pct_young_adult, 3) / 100;
    return 0;
  }

  function activityMult(settings, activity) {
    var a = String(activity || 'normal').trim().toLowerCase();
    if (a === 'light') return num(settings.activity_light_multiplier, 85) / 100;
    if (a === 'active') return num(settings.activity_active_multiplier, 120) / 100;
    return num(settings.activity_normal_multiplier, 100) / 100;
  }

  function goalMult(settings, goal) {
    var g = String(goal || 'maintain').trim().toLowerCase();
    if (g === 'lean_out' || g === 'lean') return num(settings.goal_lean_out_multiplier, 90) / 100;
    if (g === 'gain_weight' || g === 'gain') return num(settings.goal_gain_weight_multiplier, 110) / 100;
    return num(settings.goal_maintain_multiplier, 100) / 100;
  }

  /** Adult base feed rate (fraction of body weight/day) by weight band. */
  function feedRateFor(settings, weightLb) {
    var w = Number(weightLb || 0);
    if (w <= 5) return num(settings.feed_rate_1_5_pct, 5) / 100;
    if (w <= 10) return num(settings.feed_rate_6_10_pct, 4) / 100;
    if (w <= 20) return num(settings.feed_rate_11_20_pct, 3) / 100;
    if (w <= 40) return num(settings.feed_rate_21_40_pct, 2.5) / 100;
    if (w <= 80) return num(settings.feed_rate_41_80_pct, 2.25) / 100;
    return num(settings.feed_rate_81_plus_pct, 2) / 100;
  }

  /**
   * Raw (un-snapped) daily intake in lb for one dog.
   * dog: { weight, birthdate?, life_stage|stage, activity, goal }
   *
   * Stage comes from `birthdate` when present (dogs age automatically),
   * else the stored life_stage. Growth stages (young_puppy / growing_puppy /
   * juvenile, plus legacy "puppy") feed a direct age-based percent of current
   * body weight — puppies are actively growing and necessarily active, so
   * the goal and activity multipliers are skipped for them. Adult stages
   * feed the weight-band base rate x senior multiplier x activity x goal.
   */
  function computeDogDailyLb(dog, settings) {
    var d = dog || {};
    var w = Number(d.weight || 0);
    if (!(w > 0)) return 0;
    var stage = resolveLifeStage(d, settings);
    var growthPct = stageFeedRate(settings, stage);
    if (growthPct > 0) {
      // Young Adult tapers down toward the adult bands but never below them —
      // small breeds' adult band rate (4-5%) exceeds the taper percent.
      if (String(stage).toLowerCase() === 'young_adult') {
        growthPct = Math.max(growthPct, feedRateFor(settings, w));
      }
      return w * growthPct;
    }
    return w * feedRateFor(settings, w) *
      stageMult(settings, stage) *
      activityMult(settings, d.activity) *
      goalMult(settings, d.goal);
  }

  /**
   * Per-dog portion: raw intake snapped to the ladder.
   * Returns { rawOz, snappedOz, dailyLb, perMealOz }.
   */
  function computeDogPortion(dog, settings) {
    var ladder = parsePortionLadder(settings && settings.portion_ladder_oz);
    var policy = (settings && settings.portion_snap_policy) || 'nearest';
    var rawLb = computeDogDailyLb(dog, settings);
    var rawOz = rawLb * 16;
    var snappedOz = snapOz(rawOz, ladder, policy);
    var mealsN = Math.max(1, Math.floor(num(dog && dog.meals, 2)));
    return {
      rawOz: rawOz,
      snappedOz: snappedOz,
      dailyLb: snappedOz / 16,
      perMealOz: mealsN > 0 ? snappedOz / mealsN : snappedOz
    };
  }

  /* ------------------------------------------------------------------ */
  /* Freezer profiles                                                    */
  /* ------------------------------------------------------------------ */

  function freezerProfiles(settings) {
    return [
      { key: 'standard', capLb: num(settings.freezer_cap_standard_lb, 36), label: 'Standard freezer' },
      { key: 'extra', capLb: num(settings.freezer_cap_extra_lb, 72), label: 'Extra freezer space' },
      { key: 'large', capLb: num(settings.freezer_cap_large_lb, 108), label: 'Large/dedicated freezer' }
    ];
  }

  function freezerCapLb(settings, profile) {
    var p = String(profile || 'standard').trim().toLowerCase();
    if (p === 'extra') return num(settings.freezer_cap_extra_lb, 72);
    if (p === 'large' || p === 'dedicated') return num(settings.freezer_cap_large_lb, 108);
    return num(settings.freezer_cap_standard_lb, 36);
  }

  function requiredFreezer(settings, deliveryLb) {
    var profiles = freezerProfiles(settings);
    for (var i = 0; i < profiles.length; i++) {
      if (deliveryLb <= profiles[i].capLb) return profiles[i];
    }
    return { key: 'over', capLb: Infinity, label: 'Beyond largest freezer' };
  }

  /* ------------------------------------------------------------------ */
  /* Cadence list / rounding / bags (legacy-compatible)                  */
  /* ------------------------------------------------------------------ */

  function parseCadenceWeeks(raw) {
    if (!raw) return null;
    var list = String(raw)
      .split(/[,\s]+/)
      .map(function (x) { return Math.floor(Number(x)); })
      .filter(function (x) { return x > 0 && x <= 52; });
    return list.length ? list : null;
  }

  function buildCadenceList(settings) {
    var enabled = parseCadenceWeeks(settings && settings.cadence_weeks_enabled);
    if (!enabled) return DEFAULT_CADENCES.slice();
    var map = {};
    DEFAULT_CADENCES.forEach(function (c) { map[c.weeks] = c; });
    return enabled
      .map(function (w) { return map[w]; })
      .filter(Boolean);
  }

  function roundDeliveryLb(x, settings) {
    var mode = String((settings && settings.delivery_round_mode) || 'ceil').trim().toLowerCase();
    return mode === 'nearest' ? Math.round(x) : Math.ceil(x);
  }

  /**
   * Combine unrounded delivery lb across dogs, round the tote once, then
   * assign whole pounds that sum to that pack total.
   *
   * shares: [{ id, exactLb|dailyLb, minLb?, isGrowth? }]
   * Remainder pounds go to the largest leftover fraction; ties prefer a
   * growth-stage dog, then the smaller eater (1 lb is a bigger share),
   * then a stable id.
   */
  function allocatePackDeliveryLb(shares, settings, days) {
    days = Math.max(1, Number(days) || 1);
    var list = (Array.isArray(shares) ? shares : []).map(function (s, i) {
      var exact = Number(s && s.exactLb);
      if (!(exact > 0) && s && s.dailyLb != null) exact = Number(s.dailyLb) * days;
      if (!(exact > 0)) exact = 0;
      return {
        id: s && s.id != null ? String(s.id) : String(i),
        index: i,
        exactLb: exact,
        minLb: Math.max(0, Math.floor(Number(s && s.minLb) || 0)),
        isGrowth: !!(s && s.isGrowth)
      };
    });
    var exactPack = list.reduce(function (sum, s) { return sum + s.exactLb; }, 0);
    var packLb = exactPack > 0 ? roundDeliveryLb(exactPack, settings) : 0;
    var floors = list.map(function (s) {
      return Math.max(s.minLb, Math.floor(s.exactLb));
    });
    var used = floors.reduce(function (sum, n) { return sum + n; }, 0);
    if (packLb < used) packLb = used;
    var leftover = Math.max(0, packLb - used);
    var order = list.map(function (s, i) {
      var naturalFloor = Math.floor(s.exactLb);
      return {
        i: i,
        frac: s.exactLb - naturalFloor,
        isGrowth: s.isGrowth,
        rel: s.exactLb > 0 ? (1 / s.exactLb) : 0,
        id: s.id
      };
    }).sort(function (a, b) {
      if (b.frac !== a.frac) return b.frac - a.frac;
      if (a.isGrowth !== b.isGrowth) return a.isGrowth ? -1 : 1;
      if (b.rel !== a.rel) return b.rel - a.rel;
      if (a.id < b.id) return -1;
      if (a.id > b.id) return 1;
      return a.i - b.i;
    });
    var k;
    for (k = 0; k < leftover; k++) {
      if (!order.length) break;
      floors[order[k % order.length].i] += 1;
    }
    var allocations = {};
    var dogs = list.map(function (s, i) {
      allocations[s.id] = floors[i];
      return {
        id: s.id,
        exactLb: s.exactLb,
        lb: floors[i],
        minLb: s.minLb,
        isGrowth: s.isGrowth
      };
    });
    return {
      packLb: packLb,
      exactPackLb: exactPack,
      allocations: allocations,
      dogs: dogs
    };
  }

  function bagSplit(amount, cap) {
    amount = Math.max(0, Math.floor(Number(amount || 0)));
    cap = Math.max(1, Math.floor(Number(cap || 0)));
    if (!amount) return { count: 0, sizes: [] };
    var count = Math.ceil(amount / cap);
    var base = Math.floor(amount / count);
    var rem = amount - base * count;
    var sizes = [];
    for (var i = 0; i < count; i++) sizes.push(base + (i < rem ? 1 : 0));
    return { count: count, sizes: sizes };
  }

  /* ------------------------------------------------------------------ */
  /* Pack (household) evaluation                                         */
  /* ------------------------------------------------------------------ */

  /**
   * Evaluate the household pack.
   *
   * dogs: array of { name?, weight, life_stage|stage, activity, goal, meals?,
   *                  portion_oz? } — when `portion_oz` is set (customer chose a
   *                  rung in the portal) it overrides the computed snap.
   * settings: theme settings object (configurator v2 naming).
   * freezerProfile: 'standard' | 'extra' | 'large' — household selection.
   *
   * Returns {
   *   dogs: [{ dog, rawOz, snappedOz, chosenOz, dailyLb, perMealOz }],
   *   totalDailyOz, totalDailyLb, weeklyLb,
   *   cadences: [{ key, label, weeks, days, lbs, bags, bagSizes, inWindow,
   *                reasons, freezerRequired, fitsSelected, recommended }],
   *   recommended,          // best fit for selected freezer (or least-volume fallback)
   *   requiresLargerFreezer // true when recommended doesn't fit selection
   * }
   */
  function evaluatePack(dogs, settings, freezerProfile) {
    var list = Array.isArray(dogs) ? dogs.filter(Boolean) : [];
    var minLb = num(settings.min_delivery_lb, 18);
    var maxDays = num(settings.max_delivery_interval_days, 70);
    var bagCap = num(settings.bag_capacity_lb, 20);
    var selCap = freezerCapLb(settings, freezerProfile);
    var ladder = parsePortionLadder(settings && settings.portion_ladder_oz);
    var policy = (settings && settings.portion_snap_policy) || 'nearest';

    var perDog = list.map(function (dog) {
      var portion = computeDogPortion(dog, settings);
      var override = Number(dog && dog.portion_oz);
      var chosenOz = isFinite(override) && override > 0
        ? snapOz(override, ladder, 'nearest')
        : portion.snappedOz;
      var mealsN = Math.max(1, Math.floor(num(dog && dog.meals, 2)));
      return {
        dog: dog,
        rawOz: portion.rawOz,
        snappedOz: portion.snappedOz,
        chosenOz: chosenOz,
        dailyLb: chosenOz / 16,
        perMealOz: chosenOz / mealsN
      };
    });

    var totalDailyOz = perDog.reduce(function (s, p) { return s + p.chosenOz; }, 0);
    var totalDailyLb = totalDailyOz / 16;
    var weeklyLb = totalDailyLb * 7;

    var cadences = buildCadenceList(settings).map(function (c) {
      var lbs = roundDeliveryLb(totalDailyLb * c.days, settings);
      var reasons = [];
      if (lbs < minLb) reasons.push('min');
      if (c.days > maxDays) reasons.push('max');
      var inWindow = reasons.length === 0;
      var req = requiredFreezer(settings, lbs);
      var split = bagSplit(lbs, bagCap);
      return {
        key: c.key,
        label: c.label,
        weeks: c.weeks,
        days: c.days,
        lbs: lbs,
        bags: split.count,
        bagSizes: split.sizes,
        inWindow: inWindow,
        reasons: reasons,
        freezerRequired: req,
        fitsSelected: lbs <= selCap,
        recommended: false
      };
    });

    var inWindow = cadences.filter(function (b) { return b.inWindow; });
    var fits = inWindow.filter(function (b) { return b.fitsSelected; });
    var recommended = null;
    if (fits.length) {
      recommended = fits.reduce(function (a, b) { return b.days > a.days ? b : a; });
    } else if (inWindow.length) {
      // Nothing fits the selected freezer: fall back to the least-volume option.
      recommended = inWindow.reduce(function (a, b) { return b.days < a.days ? b : a; });
    }
    if (recommended) recommended.recommended = true;

    return {
      dogs: perDog,
      totalDailyOz: totalDailyOz,
      totalDailyLb: totalDailyLb,
      weeklyLb: weeklyLb,
      cadences: cadences,
      recommended: recommended,
      requiresLargerFreezer: !!(recommended && !recommended.fitsSelected)
    };
  }

  /* ------------------------------------------------------------------ */
  /* Legacy OttoCfgV2Cadence surface (uniform-grid daily rounding)       */
  /* ------------------------------------------------------------------ */

  function roundDailyIntake(rawDaily, weightLb, settings) {
    var mode = String((settings && settings.daily_round_mode) || 'half').trim().toLowerCase();
    var w = Number(weightLb || 0);
    var x = Number(rawDaily || 0);
    if (!(x > 0)) return 0;

    if (mode === 'ladder' || mode === 'portion-ladder') {
      var ladder = parsePortionLadder(settings && settings.portion_ladder_oz);
      return snapOz(x * 16, ladder, (settings && settings.portion_snap_policy) || 'nearest') / 16;
    }

    if (mode === 'portion') {
      var allowTq = !!(settings && settings.allow_three_quarter_daily);
      if (x <= 0.5) return 0.5;
      if (x <= 1) return 1;
      if (allowTq && x <= 1.5) return 1.5;
      return Math.ceil(x);
    }

    var step = 2;
    if (mode === 'quarter') step = 4;
    else if (mode === 'hybrid') {
      var hybridMax = num(settings.hybrid_small_dog_max_lb, 30);
      step = w <= hybridMax ? 4 : 2;
    }
    return Math.round(x * step) / step;
  }

  function evaluateCadence(dailyLb, settings, freezerProfile) {
    var minLb = num(settings.min_delivery_lb, 18);
    var maxDays = num(settings.max_delivery_interval_days, 70);
    var cap = freezerCapLb(settings, freezerProfile);
    var bagCap = num(settings.bag_capacity_lb, 20);
    var cadences = buildCadenceList(settings);
    var daily = Math.max(0, Number(dailyLb || 0));

    var buckets = cadences.map(function (c) {
      var rawAmount = daily * c.days;
      var amount = roundDeliveryLb(rawAmount, settings);
      var reasons = [];
      if (amount < minLb) reasons.push('min');
      if (c.days > maxDays) reasons.push('max');
      var weeklyExempt = settings.weekly_exempt_from_cap !== false;
      if (amount > cap && !(c.key === 'weekly' && weeklyExempt)) reasons.push('cap');
      var split = bagSplit(amount, bagCap);
      return {
        key: c.key,
        label: c.label,
        weeks: c.weeks,
        days: c.days,
        rawAmount: rawAmount,
        lbs: amount,
        feasible: reasons.length === 0,
        reasons: reasons,
        bags: split.count,
        bagSizes: split.sizes
      };
    });

    var feasible = buckets.filter(function (b) { return b.feasible; });
    var recommended = feasible.length
      ? feasible.reduce(function (a, b) { return b.days > a.days ? b : a; })
      : null;

    var status = 'ok';
    if (!recommended && daily > 0) {
      var longest = buckets[buckets.length - 1];
      if (longest && longest.lbs < minLb) status = 'small';
    }

    return {
      daily: daily,
      buckets: buckets,
      feasible: feasible,
      recommended: recommended,
      status: status,
      minLb: minLb,
      maxDays: maxDays,
      freezerCapLb: cap
    };
  }

  function applySmallDogPolicy(ev, settings) {
    if (ev.feasible.length || ev.status !== 'small') return ev.feasible.slice();
    var policy = String((settings && settings.small_dog_policy) || 'soft-floor').trim().toLowerCase();
    var longest = ev.buckets.length ? ev.buckets[ev.buckets.length - 1] : null;
    if (!longest) return [];

    if (policy === 'soft-floor' && longest.reasons.indexOf('min') >= 0 && longest.reasons.length === 1) {
      return [longest];
    }
    if (policy === 'overship') {
      var bagCap = num(settings.bag_capacity_lb, 20);
      var split = bagSplit(ev.minLb, bagCap);
      return [{
        key: longest.key,
        label: longest.label,
        weeks: longest.weeks,
        days: longest.days,
        lbs: ev.minLb,
        feasible: true,
        reasons: [],
        bags: split.count,
        bagSizes: split.sizes
      }];
    }
    return [];
  }

  /* ------------------------------------------------------------------ */
  /* Cadence display policy                                              */
  /* ------------------------------------------------------------------ */

  /**
   * Customers choose from a small ladder of rungs (1/2/4/6/8 weeks) they can
   * actually price in their head. The minimum viable cadence rounds UP to the
   * nearest rung, so the entry option always sits comfortably above the
   * delivery-minimum floor (better shipment economics than a bare-minimum
   * drop, and no awkward 3/5/7-week rhythms). Cadences past 8 weeks (9-12)
   * exist only so tiny packs have a viable plan: when the minimum itself is
   * past 8 weeks, that single option is all we show. Sizing math and
   * minimum-frequency suggestions keep using the full evaluated window; this
   * trims what renders, nothing else.
   *
   *   min 1 -> 1,2,4,6,8 · min 2 -> 2,4,6,8 · min 3-4 -> 4,6,8
   *   min 5-6 -> 6,8 · min 7-8 -> 8 · min 9-12 -> single option
   */
  var CADENCE_DISPLAY_MAX_WEEKS = 8;
  var CADENCE_DISPLAY_RUNG_WEEKS = [1, 2, 4, 6, 8];

  function curateCadenceChoices(options) {
    var list = (options || []).slice().sort(function (a, b) { return a.weeks - b.weeks; });
    if (list.length <= 1) return list;
    var minWeeks = list[0].weeks;
    if (minWeeks > CADENCE_DISPLAY_MAX_WEEKS) return [list[0]];
    // Floor = smallest rung at or above the minimum that's actually available
    // (max-days / freezer-cap filtering may have removed a rung upstream).
    var floor = null;
    CADENCE_DISPLAY_RUNG_WEEKS.forEach(function (w) {
      if (floor != null || w < minWeeks) return;
      if (list.some(function (o) { return o.weeks === w; })) floor = w;
    });
    if (floor == null) return [list[0]];
    return list.filter(function (o) {
      return o.weeks >= floor && CADENCE_DISPLAY_RUNG_WEEKS.indexOf(o.weeks) >= 0;
    });
  }

  /**
   * Curated choices from an evaluatePack() result. Returns copies with the
   * `recommended` flag re-picked among what's actually displayed (longest
   * that fits the selected freezer), so the badge never points at a hidden
   * option. ev.recommended itself is left untouched for freezer messaging.
   */
  function displayCadences(ev) {
    var inWindow = ((ev && ev.cadences) || []).filter(function (c) { return c.inWindow; });
    var chosen = curateCadenceChoices(inWindow);
    var best = null;
    chosen.forEach(function (c) {
      if (c.fitsSelected && (!best || c.days > best.days)) best = c;
    });
    if (!best && chosen.length) best = chosen[0];
    return chosen.map(function (c) {
      var copy = {};
      for (var k in c) if (Object.prototype.hasOwnProperty.call(c, k)) copy[k] = c[k];
      copy.recommended = c === best;
      return copy;
    });
  }

  function getCadenceOptions(dailyLb, settings, freezerProfile) {
    var ev = evaluateCadence(dailyLb, settings, freezerProfile);
    var chosen = ev.feasible.length ? ev.feasible : applySmallDogPolicy(ev, settings);
    return curateCadenceChoices(chosen).map(function (b) {
      return {
        key: b.key,
        label: b.label,
        weeks: b.weeks,
        days: b.days,
        lbs: b.lbs,
        bags: b.bags,
        bagSizes: b.bagSizes
      };
    });
  }

  /* ------------------------------------------------------------------ */
  /* Exports                                                             */
  /* ------------------------------------------------------------------ */

  var OttoEngine = {
    DEFAULT_CADENCES: DEFAULT_CADENCES,
    DEFAULT_PORTION_LADDER_OZ: DEFAULT_PORTION_LADDER_OZ,
    // portion ladder
    parsePortionLadder: parsePortionLadder,
    snapOz: snapOz,
    // age + life stage
    stageBoundsMonths: stageBoundsMonths,
    ageMonthsFromBirthdate: ageMonthsFromBirthdate,
    breedSizeClass: breedSizeClass,
    sizeClassFromWeight: sizeClassFromWeight,
    sizeClassForDog: sizeClassForDog,
    stageFromAgeMonths: stageFromAgeMonths,
    birthdateForStage: birthdateForStage,
    resolveLifeStage: resolveLifeStage,
    isGrowthStage: isGrowthStage,
    recommendedMealsPerDay: recommendedMealsPerDay,
    stageLabel: stageLabel,
    formatAgeMonths: formatAgeMonths,
    // per-dog
    computeDogDailyLb: computeDogDailyLb,
    computeDogPortion: computeDogPortion,
    // pack
    evaluatePack: evaluatePack,
    allocatePackDeliveryLb: allocatePackDeliveryLb,
    freezerProfiles: freezerProfiles,
    requiredFreezer: requiredFreezer,
    // shared utilities + legacy surface
    buildCadenceList: buildCadenceList,
    freezerCapLb: freezerCapLb,
    roundDeliveryLb: roundDeliveryLb,
    roundDailyIntake: roundDailyIntake,
    bagSplit: bagSplit,
    evaluateCadence: evaluateCadence,
    getCadenceOptions: getCadenceOptions,
    curateCadenceChoices: curateCadenceChoices,
    displayCadences: displayCadences
  };

  global.OttoEngine = OttoEngine;
  // Legacy alias: pages that load only otto-engine.js still get the
  // OttoCfgV2Cadence API. Never clobber the original file if present.
  if (!global.OttoCfgV2Cadence) {
    global.OttoCfgV2Cadence = OttoEngine;
  }
})(typeof window !== 'undefined' ? window : this);
